import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { loanApplicationSignalRConnection } from 'app/services/signalR';

import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationSignalREventType } from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getConditionalObject } from 'utils/data';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { updatePricingStatusAction } from 'store/loanApplications/actions';
import { selectLoanPricingEngineType } from 'store/loanApplications/selectors';

import { DELAY_CONNECTION_CHECK } from './constants';
import { getIncludeByPath } from './utils';

export const usePricingStatus = ({ applicationId, isDataLoaded }) => {
  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const [isSignalRConnected, setIsSignalRConnected] = useState(
    loanApplicationSignalRConnection.isConnected,
  );

  const companyId = useSelector(selectMasterCompanyId);
  const pricingEngineType = useSelector(selectLoanPricingEngineType) ?? null;

  // Ref: always-fresh pricingEngineType without triggering the effect (feedback loop safety net)
  const pricingEngineTypeRef = useRef(pricingEngineType);
  pricingEngineTypeRef.current = pricingEngineType;

  const prevIncludeRef = useRef(null);

  const include = useMemo(() => {
    const newInclude = getIncludeByPath(pathname);
    const prevInclude = prevIncludeRef.current;

    if (prevInclude && !newInclude.some(item => !prevInclude.includes(item))) {
      return prevInclude;
    }

    prevIncludeRef.current = newInclude;

    return newInclude;
  }, [pathname]);

  const fetchPricingStatus = useCallback(
    (signalMetadata, signalEventApplicationId) => {
      const signalApplicationId = signalMetadata?.applicationId || signalEventApplicationId;

      if (
        !companyId ||
        !applicationId ||
        (signalApplicationId && signalApplicationId !== applicationId)
      ) {
        return;
      }

      const pricingEngineType = pricingEngineTypeRef.current;

      dispatch(
        updatePricingStatusAction({
          companyId,
          applicationId,
          include,
          ...getConditionalObject(pricingEngineType, { pricingEngineType }),
        }),
      ).catch(Error);
    },
    [dispatch, companyId, applicationId, include],
  );

  useEffectSafeStrictMode(() => {
    if (!isExternalLoanLockEnabled || !isDataLoaded) {
      return;
    }

    fetchPricingStatus();
  }, [isExternalLoanLockEnabled, isDataLoaded, fetchPricingStatus]);

  useEffect(() => {
    if (
      !isExternalLoanLockEnabled ||
      !isSignalRConnected ||
      !loanApplicationSignalRConnection.isConnected
    ) {
      return;
    }

    loanApplicationSignalRConnection.on(
      LoanApplicationSignalREventType.PRICING_STATUS_CHANGED,
      fetchPricingStatus,
    );

    loanApplicationSignalRConnection.on(
      LoanApplicationSignalREventType.PENDING_CHANGES_SAVED,
      fetchPricingStatus,
    );

    return () => {
      if (loanApplicationSignalRConnection.isConnected) {
        loanApplicationSignalRConnection.off(
          LoanApplicationSignalREventType.PRICING_STATUS_CHANGED,
          fetchPricingStatus,
        );

        loanApplicationSignalRConnection.off(
          LoanApplicationSignalREventType.PENDING_CHANGES_SAVED,
          fetchPricingStatus,
        );
      }
    };
  }, [dispatch, isSignalRConnected, isExternalLoanLockEnabled, fetchPricingStatus]);

  useEffect(() => {
    if (isSignalRConnected) {
      return;
    }

    const intervalId = setInterval(() => {
      if (loanApplicationSignalRConnection.isConnected) {
        setIsSignalRConnected(true);
      }
    }, DELAY_CONNECTION_CHECK);

    return () => clearInterval(intervalId);
  }, [isSignalRConnected]);

  return { fetchPricingStatus };
};
